import {
  ArtifactIcon,
  InfoIcon,
  arrowDownGreyIcon,
  arrowDownWhiteIcon,
  loadingGreyIcon,
  loadingWhiteIcon,
} from '@/shared/ui/assets';
import { NoDataComponent, CustomButton, CustomSelect, CustomTooltip } from '@/shared/ui/components';
import {
  ARTIFACT_REPORT_HOVER_TEXT,
  DOUBLE_HYPHEN_BULLET,
  STATUS_REPORT,
  STATUS_REPORT_OPTIONS,
} from '@/shared/config';
import { dateToDuration, dateToTimeSince, gettextStudyType } from '@/shared/lib';
import { Spin } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import { forwardRef, memo } from 'react';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { checkNewNotiEventReport } from '@/pages/inbox-details/@x/inbox-list';
import InboxItemSkeleton from '../inboxItemSkeleton/inboxItemSkeleton';
import RecentInboxSearchComponent from '../../inboxSearchComponent/recentInboxSearchComponent';
import '../style.scss';
import { generateLabelsRecentReport } from '../../../lib/recentInboxList';
import { useRecentInboxList } from './useRecentInboxList';
import type { RecentInboxListProps } from './recentInboxList.types';

/**
 * Renders the recent-reports inbox list: the sort and filter header plus a page of rows with patient name, labels, artifact tooltip, status select, physician, event time or date of service, study type and findings. Renders only while the tab is active or data has loaded, pages displayedRecentInboxes by pageIndex and sizePerPage, shows skeleton rows until isLoadedData, and derives the placeholder's searching flag from the physician or free-text search value.
 *
 * @returns The recent inbox list element, or a falsy value while the tab is inactive and no data has loaded.
 */
const RecentInboxList = forwardRef((props: RecentInboxListProps, ref) => {
  const {
    state,
    inboxesGotoRedux,
    displayedRecentInboxes,
    sizePerPage,
    onChangeSort,
    handleChangeFilter,
    onClickReport,
    onClickStopProg,
    onChange,
    navigatePage,
  } = useRecentInboxList(props, ref);

  /**
   * Picks the suffix icon for a recent-inbox row's report-status select. A pending entry in state.listChangingStatusReport renders a spinning white loader when the requested status is Completed and a grey one otherwise, while a settled row renders the white chevron for Completed reports and the grey chevron for the rest.
   *
   * @param report - Recent inbox row whose id and statusReport decide the icon.
   * @returns The spinner or chevron element used as the select's suffix icon.
   */
  const getSuffixIcon = (report: any) => {
    /**
     * Finds the pending status change tracked for a recent-report row. Looks the report id up in state.listChangingStatusReport to learn whether a save is in flight and which status was requested.
     *
     * @returns The matching pending status-change entry, or undefined when the row has none.
     */
    const updatingReport = _.find(state.listChangingStatusReport, (item) => item.id === report.id);
    if (updatingReport?.isChangingStatus) {
      if (updatingReport.status === STATUS_REPORT.COMPLETED) {
        return (
          <span role="status" aria-label={t`Loading`}>
            <Spin
              indicator={
                <img
                  src={loadingWhiteIcon}
                  alt=""
                  style={{ animation: 'spin 1s linear infinite' }}
                />
              }
            />
          </span>
        );
      }
      return (
        <span role="status" aria-label={t`Loading`}>
          <Spin
            indicator={
              <img src={loadingGreyIcon} alt="" style={{ animation: 'spin 1s linear infinite' }} />
            }
          />
        </span>
      );
    }
    if (report.statusReport === STATUS_REPORT.COMPLETED) {
      return <img src={arrowDownWhiteIcon} alt="" />;
    }
    return <img src={arrowDownGreyIcon} alt="" />;
  };

  return (
    (props.isActive || state.isLoadedData) && (
      <div className="inbox-list">
        {(props.isActive || state.isLoadedData) && (
          <RecentInboxSearchComponent
            isLoadedData={state.isLoadedData}
            inboxes={displayedRecentInboxes}
            pageIndex={state.pageIndex}
            navigatePage={navigatePage}
            sortValue={state.sortValue}
            onChangeSort={onChangeSort}
            priority={state.priority}
            statusReports={state.statusReports}
            searchValue={state.searchValue}
            searchByValue={state.searchByValue}
            handleChangeFilter={handleChangeFilter}
            selectedTypeSeePatients={props.selectedTypeSeePatients}
          />
        )}
        {props.isActive && (
          <div
            id="inbox-list-body"
            className={classNames('inbox-list__body', !state.isLoadedData ? '--skeleton' : '')}
          >
            {displayedRecentInboxes.length ||
            (!displayedRecentInboxes.length && !state.isLoadedData) ? (
              _.map(
                state.isLoadedData
                  ? displayedRecentInboxes.slice(
                      state.pageIndex * sizePerPage.current,
                      (state.pageIndex + 1) * sizePerPage.current,
                    )
                  : state.skeletonInboxes,
                (report, index) =>
                  state.isLoadedData ? (
                    <div
                      className={classNames(
                        'inbox-item',
                        state.isLoadedData && !report.isRead && '--new',
                      )}
                      onClick={() => {
                        onClickReport(report);
                      }}
                    >
                      <div className="inbox-item__top-container">
                        <div className="left-content-container">
                          <span className="patient-name">{report.patientName}</span>
                        </div>

                        <div className="right-content-container">
                          <div className="info-container">
                            {_.map(
                              generateLabelsRecentReport(report, props.toggleDisplayDrawer),
                              (label, index) =>
                                label.isTextButton ? (
                                  <CustomButton
                                    id={`view-details-btn-${report.id}`}
                                    type="secondary"
                                    className="study-id-button --margin"
                                    tooltipTitle={t`View details`}
                                    tooltipPlacement="bottom"
                                    tooltipClassName="tooltip-study-id"
                                    icon={<ReactSVG src={InfoIcon} />}
                                    isRightIcon
                                    onClick={label.handleClick}
                                  >
                                    {label.text}
                                  </CustomButton>
                                ) : (
                                  <div
                                    key={index}
                                    className={classNames('label', '--margin', label.className)}
                                  >
                                    {label.text}
                                  </div>
                                ),
                            )}
                          </div>

                          <div className="icon-container">
                            {report.isArtifactReport && (
                              <CustomTooltip
                                placement="bottomRight"
                                title={ARTIFACT_REPORT_HOVER_TEXT}
                              >
                                <img src={ArtifactIcon} alt="Artifact Icon" />
                              </CustomTooltip>
                            )}
                            <CustomSelect
                              className={classNames(
                                report.statusReport === STATUS_REPORT.COMPLETED
                                  ? report.isSavingStatus
                                    ? 'bg-completed__saving'
                                    : 'bg-completed'
                                  : report.isSavingStatus
                                    ? 'status-report__saving'
                                    : 'status-report',
                              )}
                              name="status-report"
                              onChange={(_name, value) => onChange(value, report.id)}
                              value={report.statusReport}
                              disabled={report.isSavingStatus}
                              options={STATUS_REPORT_OPTIONS}
                              size="middle"
                              showSearch={false}
                              allowClear={false}
                              placement="bottomRight"
                              onClick={onClickStopProg}
                              suffixIcon={getSuffixIcon(report)}
                              aria-label={t`Status report`}
                            />
                          </div>
                        </div>
                      </div>

                      {state.isShowFacilityName && (
                        <div className="inbox-item__facility-container">{report.facilityName}</div>
                      )}

                      <div className="inbox-item__bottom-container">
                        <div className="left-container">
                          <div className="info-item patient-id">
                            <div className="title">{`${t`Patient ID`}:`}</div>
                            <div className="content">
                              <CustomButton
                                id="drawer-btn"
                                className="content button-content"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  props.toggleDisplayDrawer(report, t`Patient information`);
                                }}
                                type="tertiary"
                                size="small"
                              >
                                {report.patientId}
                              </CustomButton>
                            </div>
                          </div>
                          <div className="info-item">
                            <div className="title">{`${t`Physician`}:`}</div>
                            <div className="content">{report.interprettingPhysicianName}</div>
                          </div>
                          <div className="info-item">
                            <div className="title">
                              {report.type === 'Event'
                                ? `${t`Event Time`}:`
                                : `${t`Date of service`}:`}
                            </div>
                            <div className="content">
                              {report.type === 'Event' ? report.eventTime : report.dateOfService}
                            </div>
                          </div>
                          <div className="info-item">
                            <div className="title">{`${t`Study type`}:`}</div>
                            <div className="content">{gettextStudyType(report.studyType)}</div>
                          </div>
                        </div>

                        <div className="right-container">
                          <div className="border-left-container">
                            <div className="technician-comments__title">
                              {report.isNewFormat || checkNewNotiEventReport(report)
                                ? t`Findings`
                                : t`Technician comments`}
                            </div>
                            <CustomTooltip
                              classNames={{ root: 'technician-comments__tooltip' }}
                              placement="bottomLeft"
                              mouseLeaveDelay={0}
                              mouseEnterDelay={0}
                              arrow={false}
                              title={report.technicianComments}
                            >
                              <div
                                id={`technician-comments-${report.id}`}
                                className="technician-comments__content"
                              >
                                <span>{report.technicianComments || DOUBLE_HYPHEN_BULLET}</span>
                              </div>
                            </CustomTooltip>
                          </div>
                          <div className="time justify-end">
                            <div>{dateToTimeSince(dateToDuration(report.date))}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <InboxItemSkeleton key={index} />
                  ),
              )
            ) : (
              <NoDataComponent
                isSearching={
                  state.searchByValue === 'physicianId'
                    ? !_.isEmpty(inboxesGotoRedux.search.physicianSearchValue)
                    : !!state.searchValue
                }
              />
            )}
          </div>
        )}
      </div>
    )
  );
});

export default memo(RecentInboxList);
